import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ticketsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'teacher_academic', label: 'Teacher / academic' },
  { value: 'finance', label: 'Finance' },
  { value: 'sales_enrollment', label: 'Sales / enrollment' },
  { value: 'ops_other', label: 'Ops / other' },
  { value: 'batch_ops', label: 'Batch / scheduling' },
];

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const CREATOR_ROLES = new Set([
  'parent_feedback_agent',
  'sales',
  'sales_supervisor',
  'country_manager',
  'admin',
  'teacher',
  'teacher_supervisor',
  'teacher_admin',
  'batch_supervisor',
  'batch_admin',
]);

const SUMMARY_ROLES = new Set(['admin', 'teacher_admin', 'sales_supervisor', 'country_manager', 'finance_admin', 'batch_supervisor', 'batch_admin']);

function statusColor(status) {
  if (status === 'open') return 'warning';
  if (status === 'in_progress') return 'info';
  if (status === 'resolved') return 'success';
  return 'default';
}

export default function TicketsPage({ inboxDefault = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(inboxDefault ? '' : '');
  const [category, setCategory] = useState('');
  const [mine, setMine] = useState(false);
  const [inbox, setInbox] = useState(inboxDefault);

  const canCreate = CREATOR_ROLES.has(user?.role);
  const canSummary = SUMMARY_ROLES.has(user?.role);

  const listQuery = useQuery(['tickets', status, category, mine, inbox], async () => {
    const params = {};
    if (status) params.status = status;
    if (category) params.category = category;
    if (mine) params.mine = true;
    if (inbox) params.inbox = true;
    const res = await ticketsAPI.list(params);
    return res.data.data || [];
  });

  const summaryQuery = useQuery(
    ['tickets-summary'],
    async () => {
      const res = await ticketsAPI.getSummary();
      return res.data.data;
    },
    { enabled: canSummary }
  );

  const rows = listQuery.data || [];
  const summary = summaryQuery.data;

  const title = useMemo(() => (inbox ? 'Ticket inbox' : 'Tickets'), [inbox]);

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" mb={2} spacing={2}>
        <Typography variant="h5">{title}</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant={inbox ? 'contained' : 'outlined'} onClick={() => setInbox(true)}>
            Inbox
          </Button>
          <Button variant={!inbox && !mine ? 'contained' : 'outlined'} onClick={() => { setInbox(false); setMine(false); }}>
            All visible
          </Button>
          <Button variant={mine ? 'contained' : 'outlined'} onClick={() => { setInbox(false); setMine(true); }}>
            My created
          </Button>
          {canCreate ? (
            <Button variant="contained" color="primary" onClick={() => navigate('/tickets/new')}>
              New ticket
            </Button>
          ) : null}
        </Stack>
      </Stack>

      {canSummary && summary ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={2} useFlexGap>
          <Chip label={`Open ${summary.byStatus?.open || 0}`} />
          <Chip label={`In progress ${summary.byStatus?.in_progress || 0}`} color="info" />
          <Chip label={`Resolved ${summary.byStatus?.resolved || 0}`} color="success" />
          <Chip label={`My assigned open ${summary.myAssignedOpen || 0}`} color="warning" />
        </Stack>
      ) : null}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          {STATUSES.map((s) => (
            <MenuItem key={s.value || 'all'} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {CATEGORIES.map((c) => (
            <MenuItem key={c.value || 'all'} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Paper>
        {listQuery.isLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Assignee / queue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      {inbox ? 'No tickets assigned to you.' : 'No tickets match filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tickets/${row.id}`)}
                  >
                    <TableCell>{row.ticket_number}</TableCell>
                    <TableCell>{row.subject}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.priority}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status} color={statusColor(row.status)} />
                    </TableCell>
                    <TableCell>{row.student_name || '—'}</TableCell>
                    <TableCell>{row.assignee_name?.trim() || row.queue_role || '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
